import React, { useState, useEffect, useContext } from 'react';
import { Banner, theme, Spinner, Text } from '@xometry/xometry_loft';
import { useGetTargetUserData } from '../../../contexts/User/UserContext';
import { NotificationContext } from '../../../contexts/Notification';
import { initiateResendEmailVerification } from '../../User/UserDisplay/helpers';
import { useLazyQuery } from '@apollo/client';
import { css } from '@emotion/react';
import {
  GetBillingAccountUserQuery,
  GetBillingAccountUserQueryResult,
  GetBillingAccountUserQueryVariables,
} from '../../../schema/generated/schema';

const container = css`
  width: 100%;
  max-width: 792px;
`;

// This component handles the logic and rendering of banners on user-settings page;
const CTABanner = () => {
  const { userData, isLoading, error } = useGetTargetUserData();
  const { addNotification } = useContext(NotificationContext);
  // the component is showed if the user has not email_address_verified
  // we show the billing message in the banner only if the user has billing accounts
  const [showBanner, setShowBanner] = useState(false);
  const [showBillingMessage, setShowBillingMessage] = useState(false);
  const verificationEmailSent = localStorage.getItem(
    'resent_verification_email_banner'
  );

  const [getTargetUserBillingAccountsData] = useLazyQuery<
    GetBillingAccountUserQueryResult,
    GetBillingAccountUserQueryVariables
  >(GetBillingAccountUserQuery);

  useEffect(() => {
    if (userData) {
      setShowBanner(
        !userData.is_logged_in_user_staff && !userData.email_address_verified
      );
    }

    if (!isLoading && !error) {
      if (
        !userData.email_address_verified &&
        verificationEmailSent !== 'true'
      ) {
        // Terms being defined as being able to pay with a PO, based on this checkout-app and monolith definition
        // https://github.com/xometry/checkout-app/blob/4ac26b053ac5002990e634e21245f25809fae221/src/features/Checkout/Checkout.jsx#L675-L678
        getTargetUserBillingAccountsData({
          variables: {
            userId: userData._id,
          },
          onCompleted: (data) => {
            const billingAccountsData =
              data?.getBillingAccountUser?.billingAccounts?.edges;
            setShowBillingMessage(
              billingAccountsData &&
                billingAccountsData.length > 0 &&
                !(
                  billingAccountsData[0].node?.status === 'ACTIVE' &&
                  (billingAccountsData[0].node?.creditLimitInCents ?? 0) > 0
                )
            );
          },
        });
      }
    }
  }, [isLoading, userData]);

  const handleVerifyEmailClick = async () => {
    const NotificationExtraOptions = {
      hasIcon: true,
      hideClose: true,
    };
    const { notificationType, notificationMessage } =
      await initiateResendEmailVerification(
        userData._id,
        userData.email_address
      );
    addNotification(
      notificationType,
      notificationMessage,
      NotificationExtraOptions
    );
    localStorage.setItem('resent_verification_email_banner', 'true');
    setShowBanner(false);
  };

  if (isLoading || error) return <Spinner />;

  return (
    <div css={container}>
      {showBanner && (
        <Banner
          css={{ borderTop: `2px solid ${theme.colors.yellows[100]}` }}
          kind={theme.kind.warning}
          bannerTitle="Your email is not verified."
          buttonText="Send Verification Email"
          onButtonClick={handleVerifyEmailClick}
        >
          <Text
            fontSize={theme.fontSizes[0]}
            kind="body-sm"
            style={{ whiteSpace: 'normal' }}
          >
            Verify your email to activate Multi-Factor Authentication
          </Text>
          {showBillingMessage && (
            <Text
              fontSize={theme.fontSizes[0]}
              kind="body-sm"
              style={{ whiteSpace: 'normal', marginLeft: '3px' }}
            >
              and set up your Billing Profile.
            </Text>
          )}
        </Banner>
      )}
    </div>
  );
};

export default CTABanner;
